
import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Mail, Clock, RefreshCw, CheckCircle } from 'lucide-react';
import { useEmailVerification } from '@/hooks/useEmailVerification';

interface EmailVerificationPromptProps {
  email: string;
  onBackToSignup: () => void;
}

export const EmailVerificationPrompt: React.FC<EmailVerificationPromptProps> = ({
  email,
  onBackToSignup
}) => {
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const { resendVerification, isResending, resendCooldown } = useEmailVerification();

  const handleResend = async () => {
    setError('');
    setSuccess(false);

    try {
      await resendVerification(email);
      setSuccess(true);
    } catch (err: any) {
      setError(err.message || 'Failed to resend verification email');
    }
  };

  return (
    <Card className="shadow-2xl border-0 bg-card/95 backdrop-blur-sm">
      <CardHeader className="text-center space-y-2">
        <div className="mx-auto w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mb-4">
          <Mail className="w-8 h-8 text-primary" />
        </div>
        <CardTitle className="text-2xl font-bold text-foreground">
          Check Your Email
        </CardTitle>
        <CardDescription className="text-muted-foreground">
          We've sent a verification link to <span className="font-medium text-primary">{email}</span>
        </CardDescription>
      </CardHeader>
      
      <CardContent className="space-y-6">
        <div className="text-center space-y-4">
          <p className="text-sm text-muted-foreground">
            Click the verification link in your email to activate your account.
          </p>
          
          <div className="bg-accent border border-accent-foreground/20 rounded-lg p-4 text-sm text-accent-foreground">
            <div className="flex items-center gap-2 mb-2">
              <Clock className="w-4 h-4" />
              <span className="font-medium">Verification links expire in 24 hours</span>
            </div>
            <p>If you don't see the email, check your spam folder or try resending.</p>
          </div>
        </div>

        {success && (
          <Alert className="border-success/20 bg-success/10">
            <CheckCircle className="h-4 w-4 text-success" />
            <AlertDescription className="text-success">
              Verification email sent successfully! Check your inbox.
            </AlertDescription>
          </Alert>
        )}

        {error && (
          <Alert className="border-destructive/20 bg-destructive/10" variant="destructive">
            <AlertDescription className="text-destructive">
              {error}
            </AlertDescription>
          </Alert>
        )}

        <div className="space-y-3">
          <Button
            onClick={handleResend}
            disabled={isResending || resendCooldown > 0}
            className="w-full bg-primary hover:bg-primary/90 text-primary-foreground"
          >
            {isResending ? (
              <>
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                Sending...
              </>
            ) : resendCooldown > 0 ? (
              <>
                <Clock className="w-4 h-4 mr-2" />
                Resend in {resendCooldown}s
              </>
            ) : (
              <>
                <Mail className="w-4 h-4 mr-2" />
                Resend Verification Email
              </>
            )}
          </Button>

          <Button
            variant="outline"
            onClick={onBackToSignup}
            className="w-full border-border text-foreground hover:bg-muted"
          >
            Back to Sign Up
          </Button>
        </div>

        <div className="text-xs text-muted-foreground text-center space-y-1">
          <p>Still having trouble?</p>
          <p>
            Make sure to check your spam folder and add{' '}
            <span className="font-medium">noreply@supabase.io</span> to your contacts.
          </p>
        </div>
      </CardContent>
    </Card>
  );
};
